module Hereabout.Feed.Json where

import Prelude

import Data.Argonaut (class DecodeJson, JsonDecodeError(..), decodeJson)
import Data.Argonaut.Decode (class DecodeJson, decodeJson, (.:), (.:?))
import Data.Bifunctor (lmap)
import Data.Either (Either(..))
import Data.Maybe as M
import DateTime.ISO (unwrapISO)
import Effect.Exception (name)
import Halogen.HTML (address)
import Hereabout.Types (Address(..), AreaCommunityDetails(..), CommunityDetails(..), CommunityFeed(..), Content(..), Event(..), EventType(..), EventWithComments(..), GeoPosition(..), Comment(..), Place(..), Unsantised(..), User(..), Venue(..), VenueFeed(..))
import Parsing (Parser)
import Parsing as P
import Parsing.String as PS
import Parsing.String.Basic as PSB
import Web.Event.Event (timeStamp)
import Web.HTML.HTMLMetaElement (content)

newtype HAPIJson a = HAPIJson a


pGeoPosition ∷ Parser String GeoPosition
pGeoPosition = do
  void $ PS.string "POINT("
  long <- PSB.number
  void PSB.space
  lat <- PSB.number
  void $ PS.string ")"
  pure $ GeoPosition { latitude : lat, longitude : long }

instance decodeJsonGeoPosition :: DecodeJson (HAPIJson GeoPosition) where
  decodeJson json = do
    pos <- decodeJson json
    map HAPIJson $ decodeJson
             >=> flip P.runParser (pGeoPosition :: P.Parser String GeoPosition)
             >>> lmap (TypeMismatch <<< P.parseErrorMessage) $ pos
    -- latitude <- pos .: "latitude" 
    -- longitude <- pos .: "longitude"
    -- pure $ HAPIJson $ GeoPosition {latitude : latitude, longitude : longitude}

instance decodeJsonAddress :: DecodeJson (HAPIJson Address) where
  decodeJson json = do
    pos <- decodeJson json
    addressLine1 <- pos .: "addressLine1"
    addressLine2 <- pos .: "addressLine2"
    addressLine3 <- pos .: "addressLine3"
    addressLocality <- pos .: "addressLocality"
    addressCounty <- pos .: "addressCounty"
    addressPostalCode <- pos .: "addressPostalCode"
    addressCountry <- pos .: "addressCountry"
    pure $ HAPIJson $ Address
      { addressLine1 : addressLine1
      , addressLine2 : addressLine2
      , addressLine3 : addressLine3
      , addressLocality : addressLocality
      , addressCounty : addressCounty
      , addressPostalCode : addressPostalCode
      , addressCountry : addressCountry
      }

instance decodeJsonContent :: DecodeJson (HAPIJson Content) where
  decodeJson json = do
    pos <- decodeJson json
    contentType <- pos .: "type"
    case contentType of
      "content_markdown" -> do
        data' <- pos .: "data"
        Right $ HAPIJson $ MarkdownContent data'
      "content_paragraphs" -> do
        data' <- pos .: "data"
        Right $ HAPIJson $ ParagraphsContent data'
      _ -> Left $ TypeMismatch ("Invalid content type: " <> contentType)

instance decodeJsonVenue :: DecodeJson (HAPIJson Venue) where
  decodeJson json = do
    pos <- decodeJson json
    (HAPIJson address) <- pos .: "address"
    boundaries <- pos .: "boundaries"
    (HAPIJson content) <- pos .: "content"
    id <- pos .: "id"
    image <- pos .: "image"
    name <- pos .: "name"
    phoneNumber <- pos .:? "phoneNumber"
    website <- pos .:? "website"
    pure $ HAPIJson $ Venue 
      { address : address
      , boundaries : boundaries
      , content : content
      , id : id
      , image : image
      , name : name
      , phoneNumber : phoneNumber
      , website : website
      }

instance decodeJsonPlace:: DecodeJson (HAPIJson Place) where
  decodeJson json = do
    pos <- decodeJson json
    contentType <- pos .: "type"
    case contentType of
      "gps_pos_place" -> do
        (HAPIJson data') <- pos .: "data"
        pure $ HAPIJson $ GPSPosPlace data'
      "venue_place" -> do
        (HAPIJson data') <- pos .: "data"
        pure $ HAPIJson $ VenuePlace data'
      _ -> Left $ TypeMismatch ("Invalid content type: " <> contentType)

instance decodeJsonEventType :: DecodeJson (HAPIJson EventType) where
  decodeJson json = do
    pos <- decodeJson json
    name <- pos .: "name"
    icon <- pos .: "icon"
    image <- pos .: "image"
    pure $ HAPIJson $ EventType
      { name : name
      , icon : icon
      , image : image
      }

instance decodeJsonEvent :: DecodeJson (HAPIJson Event) where
  decodeJson json = do
    pos <- decodeJson json
    (HAPIJson content) <- pos .: "content"
    duration <- pos .: "duration"
    id <- pos .: "id"
    image <- pos .: "image"
    name <- pos .: "name"
    signupUrl <- pos .: "signupUrl"
    (HAPIJson place) <- pos .: "place"
    type' <- map (map (\(HAPIJson type') -> type')) $ pos .:? "type"
    timestamp <- pos .: "startTimestamp" <#> unwrapISO
    commentCount' <- pos .:? "commentCount"
    favouriteCount' <- pos .: "favouriteCount"
    pure $ HAPIJson $ Event 
      { content : content
      , duration : duration
      , id : id
      , image : image
      , name : name
      , place : place
      , signupUrl : signupUrl
      , type : type'
      , timestamp : timestamp
      , commentCount : M.fromMaybe 0 commentCount'
      , favouriteCount : favouriteCount' 
      }

instance decodeJsonUser :: DecodeJson (HAPIJson User) where
  decodeJson json = do
    pos <- decodeJson json
    id <- pos .: "id"
    fullName <- pos .: "fullName"
    imageUrl <- pos .: "imageUrl"
    pure $ HAPIJson $ User 
      { id : id
      , fullName : fullName
      , imageUrl : imageUrl
      }

instance decodeJsonComment:: DecodeJson (HAPIJson (Comment Unsantised)) where
  decodeJson json = do
    pos <- decodeJson json
    id <- pos .: "id"
    user <- map (\(HAPIJson user') -> user') $ pos .: "user"
    content <- pos .: "content"
    timestamp <- pos .: "timestamp" <#> unwrapISO
    pure $ HAPIJson $ Comment 
      { id : id
      , user : user
      , content : Unsantised {data : content}
      , timestamp : timestamp
      }

instance decodeJsonEventWithComments :: DecodeJson (HAPIJson (EventWithComments Unsantised)) where
  decodeJson json = do
    pos <- decodeJson json
    (HAPIJson content) <- pos .: "content"
    duration <- pos .: "duration"
    id <- pos .: "id"
    image <- pos .: "image"
    name <- pos .: "name"
    signupUrl <- pos .: "signupUrl"
    (HAPIJson place) <- pos .: "place"
    type' <- map (map (\(HAPIJson type') -> type')) $ pos .:? "type"
    timestamp <- pos .: "startTimestamp" <#> unwrapISO
    comments' <- map (map (map (\(HAPIJson msg') -> msg'))) $ pos .:? "comments"
    favouriteCount' <- pos .: "favouriteCount"
    isFavourite' <- pos .:? "isFavourite"
    eventInstances' <- pos .: "eventInstances" <#> (map unwrapISO)
    pure $ HAPIJson $ EventWithComments
      { content : content
      , duration : duration
      , id : id
      , image : image
      , name : name
      , place : place
      , signupUrl : signupUrl
      , type : type'
      , timestamp : timestamp
      , comments :  M.fromMaybe [] comments'
      , favouriteCount : favouriteCount'
      , isFavourite : isFavourite'
      , eventInstances : eventInstances'
      }

instance decodeJsonVenueFeed :: DecodeJson (HAPIJson VenueFeed) where
  decodeJson json = do
    pos <- decodeJson json
    (HAPIJson address) <- pos .: "address"
    boundaries <- pos .: "boundaries"
    (HAPIJson content) <- pos .: "content"
    events <- map (map (\(HAPIJson content') -> content')) $ pos .: "events"
    id <- pos .: "id"
    image <- pos .: "image"
    name <- pos .: "name"
    phoneNumber <- pos .:? "phoneNumber"
    website <- pos .:? "website"
    pure $ HAPIJson $ VenueFeed 
      { address : address
      , boundaries : boundaries
      , content : content
      , events : events
      , id : id
      , image : image
      , name : name
      , phoneNumber : phoneNumber
      , website : website
      }

instance decodeJsonCommunityFeed :: DecodeJson (HAPIJson CommunityFeed) where
  decodeJson json = do
    pos <- decodeJson json
    (HAPIJson content) <- pos .: "content"
    id <- pos .: "id"
    image <- pos .: "image"
    name <- pos .: "name"
    venues <- map (map (\(HAPIJson content') -> content')) $ pos .: "venues"
    events <- map (map (\(HAPIJson content') -> content')) $ pos .: "events"
    repoUrl <- pos .: "repoUrl"
    pure $ HAPIJson $ CommunityFeed 
      { content : content
      , events : events
      , id : id
      , image : image
      , name : name
      , venues : venues
      , repoUrl : repoUrl
      }

instance decodeJsonCommunityDetails :: DecodeJson (HAPIJson CommunityDetails) where
  decodeJson json = do
    pos <- decodeJson json
    id <- pos .: "id"
    name <- pos .: "name"
    image <- pos .:? "image"
    (HAPIJson content) <- pos .: "content"
    pure $ HAPIJson $ CommunityDetails 
      { communityId : id
      , name : name
      , image : image
      , content : content
      }

instance decodeJsonAreaCommunityDetails :: DecodeJson (HAPIJson AreaCommunityDetails) where
  decodeJson json = do
    pos <- decodeJson json
    id <- pos .: "id"
    name <- pos .: "name"
    areaCommunities <- map (map (\(HAPIJson content') -> content')) $ pos .: "communities"
    pure $ HAPIJson $ AreaCommunityDetails 
      { acdAreaId : id
      , acdAreaName : name
      , acdAreaCommunities : areaCommunities
      }
